<script lang="ts">
    import type {CollectionsTreeListNode} from './kp-record-collections-tree-list.presenter';
    import {createEventDispatcher, getContext} from 'svelte';
    import {CONTEXT_PATH_KEY, LOCALIZE_KEY} from '../../svelte/utils';
    import KpDropdownButton from '../../commons/ui-components/dropdown/KpDropdownButton.svelte';
    import type {LocalizeFunction} from '../../svelte/types';
    import KpDropdownMenuItem from '../../commons/ui-components/dropdown/KpDropdownMenuItem.svelte';
    import type {RecordCollectionsEventMap} from './events';
    import KpCollapsibleGenericPanel from '../../commons/ui-components/panel/KpCollapsibleGenericPanel.svelte';
    import KpCollapsibleGenericPanelToggle
        from '../../commons/ui-components/panel/KpCollapsibleGenericPanelToggle.svelte';
    import {isPublicCollection} from '../util';

    export let node: CollectionsTreeListNode;

    const dispatch = createEventDispatcher<RecordCollectionsEventMap>();
    const CONTEXT_PATH: string = getContext(CONTEXT_PATH_KEY);
    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);

    let disableContextMenu: boolean;
    $: disableContextMenu = !(node.canCreateSubcollection || node.editable || node.canMoveUp || node.canMoveDown || node.deletable);

    let isPublic: boolean;
    $: isPublic = isPublicCollection(node.recordCollection);

    function moveUp() {
        dispatch('moveUpItem', {item: node.recordCollection});
    }

    function moveDown() {
        dispatch('moveDownItem', {item: node.recordCollection});
    }

    function edit() {
        dispatch('editItem', {item: node.recordCollection});
    }

    function create() {
        dispatch('createItem', {item: node.recordCollection});
    }

    function remove() {
        dispatch('removeItem', {item: node.recordCollection});
    }

</script>

<template>
    <KpCollapsibleGenericPanel collapseTargetId={node.recordCollection.id}>
        <div slot="heading" class="collection-panel-heading">
            <a href="{CONTEXT_PATH}/#!/record-collections/{node.recordCollection.id}" class="collection-name panel-title">{node.recordCollection.name}</a>
            <span class="context-buttons">
                {#if isPublic}
                    <span class="glyphicon glyphicon-eye-open visibility-icon" title={localize(/* @kp-localization recordCollections.SharedCollection */ 'recordCollections.SharedCollection')}></span>
                {:else}
                    <span class="glyphicon glyphicon-eye-close visibility-icon" title={localize(/* @kp-localization recordCollections.PrivateCollection */ 'recordCollections.PrivateCollection')}></span>
                {/if}
                <KpCollapsibleGenericPanelToggle collapseTargetId={node.recordCollection.id}/>
                {#if node.showContextMenuButton}
                    <KpDropdownButton buttonSize="md" menuAlignment="right" isDisabled={disableContextMenu}>
                        <svelte:fragment slot="button">
                            <span class="glyphicon glyphicon-menu-hamburger"></span>
                        </svelte:fragment>
                        <svelte:fragment slot="menu">
                            <KpDropdownMenuItem on:click={() => create()} showWhen={node.canCreateSubcollection}>
                                <span class="glyphicon glyphicon-plus"></span>
                                {localize(/* @kp-localization recordCollections.NewSubcollection */ 'recordCollections.NewSubcollection')}
                            </KpDropdownMenuItem>
                            <KpDropdownMenuItem on:click={() => edit()} showWhen={node.editable}>
                                <span class="glyphicon glyphicon-pencil"></span>
                                {localize(/* @kp-localization commons.Uprava */ 'commons.Uprava')}
                            </KpDropdownMenuItem>
                            <KpDropdownMenuItem on:click={() => moveUp()} showWhen={node.canMoveUp}>
                                <span class="glyphicon glyphicon-arrow-up"></span>
                                {localize(/* @kp-localization commons.MoveUp */ 'commons.MoveUp')}
                            </KpDropdownMenuItem>
                            <KpDropdownMenuItem on:click={() => moveDown()} showWhen={node.canMoveDown}>
                                <span class="glyphicon glyphicon-arrow-down"></span>
                                {localize(/* @kp-localization commons.MoveDown */ 'commons.MoveDown')}
                            </KpDropdownMenuItem>
                            <KpDropdownMenuItem on:click|once={() => remove()} showWhen={node.deletable}>
                                <span class="glyphicon glyphicon-trash"></span>
                                {localize(/* @kp-localization commons.odebrat */ 'commons.odebrat')}
                            </KpDropdownMenuItem>
                        </svelte:fragment>
                    </KpDropdownButton>
                {/if}
            </span>
        </div>
        <svelte:fragment>
            {#if node.showCreatorAndDepartment}
                <div>
                    <strong>{localize(/* @kp-localization commons.autor */ 'commons.autor')}:</strong>
                    <a href="{CONTEXT_PATH}/#!/users/{node.recordCollection.owner.id}">{node.recordCollection.owner.printableName}</a>
                </div>
                <div>
                    <strong>{localize(/* @kp-localization commons.Department */ 'commons.Department')}:</strong>
                    <span>{node.recordCollection.department.text}</span>
                </div>
            {/if}
            <div>
                <strong>{localize(/* @kp-localization recordCollections.Note */ 'recordCollections.Note')}:</strong>
                {#if !!node.recordCollection.note}
                    {node.recordCollection.note}
                {/if}
            </div>
        </svelte:fragment>
    </KpCollapsibleGenericPanel>
</template>

<style lang="less">
  .collection-panel-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .collection-name {
      font-size: 1.5em;
    }

    .context-buttons {
      flex-shrink: 0;
    }
  }

  .visibility-icon {
    padding-right: 1em;
    padding-left: 1em;
  }
</style>